<template>
    <div>
        <button
            name="record-chargeback"
            class="w-full inline-flex justify-center items-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none"
            @click="openModal"
        >
            <DocumentAddIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            Record Chargeback
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <DocumentAddIcon
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Chargeback for Order #{{
                                            publicOrderId
                                        }}
                                    </DialogTitle>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />

                                    <div
                                        class="sm:col-span-2 sm:px-16 pt-2 pb-4 sm:pb-2"
                                    >
                                        <label
                                            for="date"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Chargeback Date
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <datepicker
                                                v-model="inputs.date"
                                                :ref="refs.date"
                                                id="date"
                                                name="date"
                                                :class="[
                                                    hasError(errors, 'date')
                                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                ]"
                                            />
                                        </div>
                                        <p
                                            v-if="hasError(errors, 'date')"
                                            class="mt-2 text-sm text-red-600"
                                        >
                                            {{ error(errors, "date") }}
                                        </p>
                                    </div>

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-4 sm:pb-2"
                                    >
                                        <label
                                            for="case_id"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Case ID
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <input
                                                v-model="inputs.case_id"
                                                :ref="refs.case_id"
                                                name="case_id"
                                                id="case_id"
                                                type="text"
                                                :class="[
                                                    hasError(errors, 'case_id')
                                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                ]"
                                            />
                                        </div>
                                        <p
                                            v-if="hasError(errors, 'case_id')"
                                            class="mt-2 text-sm text-red-600"
                                        >
                                            {{ error(errors, "case_id") }}
                                        </p>
                                    </div>

                                    <div
                                        class="sm:col-span-2 sm:px-16 pb-8 sm:pb-6"
                                    >
                                        <label
                                            for="amount"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Amount
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <div
                                                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                                            >
                                                <span
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'amount',
                                                        )
                                                            ? 'text-red-900'
                                                            : 'text-gray-500',
                                                        'sm:text-sm',
                                                    ]"
                                                    >$</span
                                                >
                                            </div>
                                            <input
                                                v-model="inputs.amount"
                                                name="amount"
                                                id="amount"
                                                type="number"
                                                step="0.01"
                                                :class="[
                                                    hasError(errors, 'amount')
                                                        ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                        : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                    'appearance-none block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                ]"
                                            />
                                        </div>
                                        <p
                                            v-if="hasError(errors, 'amount')"
                                            class="mt-2 text-sm text-red-600"
                                        >
                                            {{ error(errors, "amount") }}
                                        </p>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="bg-nutreance-green hover:bg-nutreance-green-darker w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { nextTick, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, DocumentAddIcon, XIcon } from "@heroicons/vue/solid";
import Datepicker from "vue3-datepicker";
import { DateTime } from "luxon";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";

const props = withDefaults(
    defineProps<{
        csrf?: string;
        errors?: any;
        old?: any;
        publicOrderId?: string;
        balance?: number;
    }>(),
    {
        balance: 0,
    },
);

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const form = ref<any>(null);
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "amount") ||
        Object.prototype.hasOwnProperty.call(props.old, "case_id") ||
        Object.prototype.hasOwnProperty.call(props.old, "date"),
);
const actionUrl = "/user/orders/" + props.publicOrderId + "/chargebacks";

const refs = {
    case_id: ref<any>(null),
    date: ref<any>(null),
};

const defaultAmount = Math.max(props.balance, 0) / 100;

const inputs = reactive({
    amount: oldInput(props.old, "amount")
        ? oldInput(props.old, "amount")
        : defaultAmount.toFixed(2),
    case_id: oldInput(props.old, "case_id") || "",
    date: oldInput(props.old, "date")
        ? DateTime.fromISO(oldInput(props.old, "date")).toJSDate()
        : new Date(),
});

function openModal() {
    open.value = true;
    nextTick(() => {
        if (refs.case_id.value) {
            refs.case_id.value.focus();
        }
    });
}
</script>
